import { useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Accordion, AccordionSet, formatNumber } from "@narmi/design_system";
import type Account from "byzantine/src/Account";
import { groupAccountsByType, type AccountGroup } from "./accountGroups";
import styles from "./NetWorthBreakdown.module.scss";

type NetWorthBreakdownProps = {
  /** Already filtered to whatever's currently selected (and non-hidden). */
  accounts: Account[];
  totalAssets: number;
  totalLiabilities: number;
};

const sumValue = (accounts: Account[]): number =>
  accounts.reduce((sum, account) => sum + account.netWorthValueAsFloat(), 0);

const AccordionHeader = ({
  isOpen,
  label,
  amount,
  className,
}: {
  isOpen: boolean;
  label: string;
  amount: number;
  className: string;
}) => (
  <div className={className}>
    <span>{label}</span>
    <span className={styles.headerEnd}>
      {formatNumber(amount, "currency")}
      <span className={`narmi-icon-chevron-${isOpen ? "up" : "down"}`} />
    </span>
  </div>
);

const CategoryGroups = ({ groups }: { groups: AccountGroup[] }) => (
  <AccordionSet behavior="exclusive">
    {groups.map((group) => (
      <Accordion
        key={group.name}
        testId="net-worth-breakdown-category"
        renderSummary={(isOpen) => (
          <AccordionHeader
            isOpen={isOpen}
            label={group.name}
            amount={sumValue(group.accounts)}
            className={styles.categoryHeader}
          />
        )}
      >
        <div className={styles.accountList}>
          {group.accounts.map((account) => (
            <div key={account.id} className={styles.accountRow}>
              <span>{account.getName()}</span>
              <span>
                {formatNumber(account.netWorthValueAsFloat(), "currency")}
              </span>
            </div>
          ))}
        </div>
      </Accordion>
    ))}
  </AccordionSet>
);

/**
 * Pure breakdown content: an "Assets & liabilities" heading plus two
 * top-level accordions, each grouping its accounts into per-category
 * accordions. Has no drawer/trigger chrome of its own — the caller decides
 * how this gets shown (e.g. inside BorderCard's in-card drawer).
 */
const NetWorthBreakdown = ({
  accounts,
  totalAssets,
  totalLiabilities,
}: NetWorthBreakdownProps) => {
  const { l10n } = useLocalization();

  const assetGroups = useMemo(
    () =>
      groupAccountsByType(
        accounts.filter((account) => account.net_worth_category === "asset")
      ),
    [accounts]
  );
  const liabilityGroups = useMemo(
    () =>
      groupAccountsByType(
        accounts.filter((account) => account.net_worth_category === "liability")
      ),
    [accounts]
  );

  return (
    <div className={styles.container} data-testid="net-worth-breakdown-content">
      <h4 className={styles.drawerTitle}>
        {l10n.getString("net-worth-breakdown-title")}
      </h4>
      <div className={styles.scrollArea}>
        <AccordionSet behavior="native">
          <Accordion
            testId="net-worth-breakdown-assets"
            renderSummary={(isOpen) => (
              <AccordionHeader
                isOpen={isOpen}
                label={l10n.getString("label-net-worth-assets")}
                amount={totalAssets}
                className={styles.topLevelHeader}
              />
            )}
          >
            <CategoryGroups groups={assetGroups} />
          </Accordion>
          <Accordion
            testId="net-worth-breakdown-liabilities"
            renderSummary={(isOpen) => (
              <AccordionHeader
                isOpen={isOpen}
                label={l10n.getString("label-net-worth-liabilities")}
                amount={totalLiabilities}
                className={styles.topLevelHeader}
              />
            )}
          >
            <CategoryGroups groups={liabilityGroups} />
          </Accordion>
        </AccordionSet>
      </div>
    </div>
  );
};

export default NetWorthBreakdown;
